import {
  ADVIEZEN,
  AFKOPPELBESLUITEN,
  MAATREGELENBESLUITEN,
  EIGENDOMSVORMEN,
  REALISATIE_STATUSSEN,
  SUBSIDIE_STATUSSEN,
  UITKOMSTEN_BEZOEK,
  type Advies,
  type Afkoppelbesluit,
  type Contactmoment,
  type Registratie,
  type UitkomstBezoek,
} from '@/types/domain';
import { nieuweRegistratie } from '@/services/registratieFactory';
import { mockWoningen } from './mockWoningen';

/**
 * Voorbeeldregistraties zodat kaart en dashboard direct gevuld zijn.
 * Deterministisch, zodat een demo altijd hetzelfde beeld geeft.
 */

function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const COACHES = ['Sanne de Wit', 'Youssef El Amrani', 'Lars Veldman', 'Priya Raghunath'];
const NAMEN = [
  'Jan Jansen',
  'Maria de Vries',
  'Ahmed Bakker',
  'Els van Dijk',
  'Piet Visser',
  'Fatima Smit',
];

/** Besluiten met gewicht: de meeste gesprekken eindigen nog zonder besluit. */
const BESLUIT_VERDELING: Afkoppelbesluit[] = [
  ...Array<Afkoppelbesluit>(4).fill('Overweegt afkoppelen'),
  ...Array<Afkoppelbesluit>(3).fill('Overweegt afkoppelen'),
  ...Array<Afkoppelbesluit>(2).fill('Koppelt niet af'),
  'Nog geen keuze gemaakt',
];

function kies<T>(random: () => number, opties: readonly T[]): T {
  return opties[Math.floor(random() * opties.length)];
}

/** Een of twee adviezen per tuin; dat is in de praktijk het normale beeld. */
function kiesAdviezen(random: () => number): Advies[] {
  const eerste = kies(random, ADVIEZEN);
  if (random() > 0.55) return [eerste];
  const tweede = kies(random, ADVIEZEN);
  return tweede === eerste ? [eerste] : [eerste, tweede];
}

function datumTerug(random: () => number, maxDagen: number): string {
  const d = new Date();
  d.setDate(d.getDate() - Math.floor(random() * maxDagen));
  return d.toISOString().slice(0, 10);
}

function datumVooruit(random: () => number, maxDagen: number): string {
  const d = new Date();
  d.setDate(d.getDate() + 1 + Math.floor(random() * maxDagen));
  return d.toISOString().slice(0, 10);
}

export function mockRegistraties(): Registratie[] {
  const random = prng(776612);
  const woningen = mockWoningen();
  const registraties: Registratie[] = [];

  // Ongeveer een kwart van de woningen heeft een registratie.
  for (let i = 0; i < woningen.length; i += 1) {
    if (random() > 0.26) continue;

    const woning = woningen[i];
    const basis = nieuweRegistratie(woning, kies(random, COACHES));
    const besluit = kies(random, BESLUIT_VERDELING);
    const gesproken = besluit !== 'Nog geen keuze gemaakt' || random() > 0.5;
    const heeftToestemming = gesproken && random() > 0.25;
    const voortuinAanwezig = random() > 0.2;
    const isUitgevoerd = besluit === 'Overweegt afkoppelen' && random() > 0.55;

    const datum = datumTerug(random, 120);
    const uitkomst: UitkomstBezoek = gesproken
      ? besluit === 'Koppelt niet af'
        ? 'Wil niet meewerken'
        : 'Gesproken met bewoner'
      : kies(random, UITKOMSTEN_BEZOEK);
    const toelichting = gesproken
      ? 'Prettig gesprek aan de deur, folder achtergelaten.'
      : 'Niemand aangetroffen, briefje in de bus gedaan.';

    // Bij ongeveer een op de vijf adressen is er later nog contact geweest,
    // zodat de demo ook adressen met meerdere contactmomenten laat zien.
    const contactmomenten: Contactmoment[] = [
      {
        id: `mock_c1_${woning.bagId}`,
        datum,
        soort: 'Bezoek aan de deur' as const,
        coach: basis.coach,
        uitkomst,
        toelichting,
      },
    ];

    if (gesproken && random() > 0.8) {
      contactmomenten.push({
        id: `mock_c2_${woning.bagId}`,
        datum: datumTerug(random, 40),
        soort: kies(random, ['Telefonisch contact', 'E-mail van de bewoner'] as const),
        coach: basis.coach,
        uitkomst: 'Gesproken met bewoner' as UitkomstBezoek,
        toelichting: 'Bewoner liet weten hoe het ervoor staat.',
      });
    }

    const laatste = contactmomenten[contactmomenten.length - 1];

    registraties.push({
      ...basis,
      id: `mock_${woning.bagId}`,
      eigendom: kies(random, EIGENDOMSVORMEN),

      contactmomenten,
      datumBezoek: laatste.datum,
      uitkomstBezoek: laatste.uitkomst,
      uitkomstToelichting: laatste.toelichting,

      bewoner: {
        naam: heeftToestemming ? kies(random, NAMEN) : '',
        telefoon: heeftToestemming
          ? `06${Math.floor(10_000_000 + random() * 89_999_999)}`
          : '',
        email: '',
        toestemmingPersoonsgegevens: heeftToestemming,
        toelichting: '',
      },

      voortuin: {
        aanwezig: voortuinAanwezig,
        advies: voortuinAanwezig ? kiesAdviezen(random) : [],
        bergingM3: voortuinAanwezig ? Number((0.3 + random() * 1.7).toFixed(1)) : null,
        toelichting: '',
      },
      achtertuin: {
        aanwezig: true,
        advies: kiesAdviezen(random),
        bergingM3: Number((0.8 + random() * 3.2).toFixed(1)),
        toelichting: '',
      },

      afkoppelbesluit: besluit,
      maatregelenbesluit: kies(random, MAATREGELENBESLUITEN),
      subsidieStatus:
        besluit === 'Overweegt afkoppelen'
          ? kies(random, SUBSIDIE_STATUSSEN)
          : 'Niet van toepassing',
      realisatieStatus: isUitgevoerd
        ? 'Uitgevoerd'
        : besluit === 'Overweegt afkoppelen'
          ? kies(random, REALISATIE_STATUSSEN.slice(0, 3))
          : 'Niet gestart',
      datumVervolg: besluit === 'Overweegt afkoppelen' ? datumVooruit(random, 21) : '',
      algemeneToelichting: '',

      syncState: 'synced',
      aangemaaktOp: new Date(Date.now() - random() * 9e9).toISOString(),
      gewijzigdOp: new Date(Date.now() - random() * 4e9).toISOString(),
    });
  }

  return registraties;
}

/** Alleen gebruikt in de legenda-preview van het dashboard. */
export const ALLE_BESLUITEN = AFKOPPELBESLUITEN;
